import { PainDetailsData, initialPainDetailsData } from '@dentalux/ui-library-core/custom/components/pain-details-form'
import { cloneDeep } from 'lodash'
import React, { createContext, useReducer, useContext, ReactNode } from 'react'

enum ActionTypes {
  setPainDetails = 'setPainDetails',
  setShowPainTreatmentForm = 'setShowPainTreatmentForm',
  clearState = 'clearState',
}

type SetPainDetails = {
  type: ActionTypes.setPainDetails
  payload: PainDetailsData
}

type SetShowPainTreatmentForm = {
  type: ActionTypes.setShowPainTreatmentForm
  payload: boolean
}

type ClearState = {
  type: ActionTypes.clearState
}

type Action = SetPainDetails | SetShowPainTreatmentForm | ClearState

type State = {
  showPainTreatmentForm: boolean
  painDetails: PainDetailsData
}

type Dispatch = (action: Action) => void

const PainDetailsContext = createContext<State | undefined>(undefined)
const PainDetailsDispatchContext = createContext<Dispatch | undefined>(undefined)

const defaultState: State = {
  showPainTreatmentForm: false,
  painDetails: initialPainDetailsData,
}

const painDetailsReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionTypes.setPainDetails:
      return { ...state, painDetails: action.payload }
    case ActionTypes.setShowPainTreatmentForm:
      return { ...state, showPainTreatmentForm: action.payload }
    case ActionTypes.clearState:
      return cloneDeep(defaultState)
  }
}

interface PainDetailsProviderProps {
  children: ReactNode
}

export const PainDetailsProvider = ({ children }: PainDetailsProviderProps) => {
  const [state, dispatch] = useReducer(painDetailsReducer, { ...defaultState })

  return (
    <PainDetailsContext.Provider value={{ ...state }}>
      <PainDetailsDispatchContext.Provider value={dispatch}>{children}</PainDetailsDispatchContext.Provider>
    </PainDetailsContext.Provider>
  )
}

export const usePainDetailsContext = () => {
  const context = useContext(PainDetailsContext)
  if (context === undefined) {
    throw new Error('usePainDetailsContext must be user within a PainDetailsProvider')
  }
  return context
}

export const usePainDetailsDispatch = () => {
  const context = useContext(PainDetailsDispatchContext)
  if (context === undefined) {
    throw new Error('usePainDetailsDispatch must be used within a PainDetailsProvider')
  }
  return context
}

export const setPainDetails = (payload: PainDetailsData): SetPainDetails => ({
  type: ActionTypes.setPainDetails,
  payload,
})

export const setShowPainTreatmentForm = (payload: boolean): SetShowPainTreatmentForm => ({
  type: ActionTypes.setShowPainTreatmentForm,
  payload,
})

export const clearState = (): ClearState => ({
  type: ActionTypes.clearState,
})
